import { useState, useEffect } from 'react';
import { useAuth, useTheme, useThemeStyles } from '@repo/shared';
import PasswordChallengePage from './PasswordChallengePage';
import './LoginPage.css';

export default function LoginPage({ onLoginSuccess }) {
  const { login } = useAuth();
  const { appTheme } = useTheme();
  
  // Apply theme CSS variables
  useThemeStyles();
  
  useEffect(() => {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = new URL(`./images/${appTheme?.logo || 'logo-default.png'}`, import.meta.url).href;
    }, [appTheme]);
    
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [challengeData, setChallengeData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);

      // Handle NEW_PASSWORD_REQUIRED challenge
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeData({
          username,
          session: result.session,
          userAttributes: result.userAttributes,
        });
        setRequiresPasswordChange(true);
        return;
      }

      // Successful login
      onLoginSuccess('LOGIN_SUCCESS', result);
    } catch (err) {
      // Parse error message
      let errorMessage = 'Login failed';
      
      if (err.message.includes('Invalid credentials')) {
        errorMessage = 'Invalid username or password';
      } else if (err.message.includes('User not found')) {
        errorMessage = 'User not found';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show password challenge component if needed
  if (requiresPasswordChange && challengeData) {
    return (
      <PasswordChallengePage
        onSuccess={onLoginSuccess}
        onCancel={() => {
          setRequiresPasswordChange(false);
          setChallengeData(null);
          setPassword('');
        }}
        challengeData={challengeData}
      />
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">
          <img 
            alt={appTheme?.appName || ''} 
            src={new URL(`../../images/${appTheme?.logo || 'logo-default.png'}`, import.meta.url).href}
            className="login-logo-img"
          />
        </div>
        
        <h1>{appTheme?.appName || ''}</h1>
        <p className="login-subtitle">Admin Portal</p>
        
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username or Email</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              required
              placeholder="Enter your username or email"
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="submit-button"
          >
            {loading ? (
              <>
                <span className="spinner">⏳</span>
                <span>Logging in...</span>
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>First time? Your temporary password was sent to your email.</p>
        </div>
      </div>
    </div>
  );
}
